import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import ProductLookup from '../components/ProductLookup.jsx';

/**
 * Временен инструмент: закачане на тонер/чип към услугите „Зареждане на
 * тонер касета", по предложение от историята на продажбите.
 *
 * Не пипа нищо автоматично — само предлага; операторът потвърждава или
 * поправя всеки ред и чак тогава се пази. Ползва се, докато цялата
 * номенклатура мине преглед — след това изписването на консумативите при
 * продажба на услугата ще тръгне само от вече записаното тук.
 */
export default function ServiceConsumables() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [onlyUnreviewed, setOnlyUnreviewed] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [propertyOptions, setPropertyOptions] = useState({});

  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get('/api/properties').then((props) => {
      const byName = Object.fromEntries(props.map((p) => [p.name, p.values]));
      setPropertyOptions({ printType: byName['Тип печат'] ?? [], color: byName['Цвят'] ?? [] });
    });
  }, []);
  async function load() {
    setLoading(true);
    const res = await api.get('/api/service-consumables');
    setServices(res.services);
    setLoading(false);
  }

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return services.filter((s) => (!onlyUnreviewed || !s.reviewed)
      && (!q || s.name.toLowerCase().includes(q)));
  }, [services, filter, onlyUnreviewed]);

  const reviewedCount = services.filter((s) => s.reviewed).length;

  return (
    <div>
      <div className="page-head">
        <h1>Тонер/чип на услугите за зареждане</h1>
        <span className="muted">
          прегледани {reviewedCount} от {services.length}
        </span>
      </div>
      <div className="note">
        Предложенията идват от историята — с какво е било изписвано на същия
        документ досега. Прегледай, поправи ако трябва, и запиши — само
        записаното се брои за потвърдено.
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div className="card" style={{ flex: '1 1 380px', maxWidth: 420 }}>
          <div className="toolbar">
            <input
              placeholder="търсене по име"
              value={filter}
              autoComplete="off"
              onChange={(e) => setFilter(e.target.value)}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={onlyUnreviewed}
                onChange={(e) => setOnlyUnreviewed(e.target.checked)}
              />
              само непрегледани
            </label>
          </div>
          <div className="table-wrap" style={{ maxHeight: 560, overflowY: 'auto' }}>
            <table className="compact">
              <thead>
                <tr>
                  <th>Услуга</th>
                  <th className="num">Продажби</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={3} className="empty">Зареждане…</td></tr>}
                {!loading && shown.length === 0 && (
                  <tr><td colSpan={3} className="empty">Няма съвпадения.</td></tr>
                )}
                {!loading && shown.map((s) => (
                  <tr
                    key={s.id}
                    onClick={() => setSelectedId(s.id)}
                    className={[
                      s.id === selectedId ? 'active' : '',
                      s.capacityCopies ? '' : 'no-capacity',
                    ].filter(Boolean).join(' ')}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{s.name}</td>
                    <td className="num">{s.salesCount}</td>
                    <td>{s.reviewed
                      ? <span className="badge ok">закачено ×{s.linkedCount}</span>
                      : <span className="badge">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ flex: '2 1 520px' }}>
          {selectedId
            ? (
              <ServiceReview
                key={selectedId}
                serviceId={selectedId}
                onSaved={load}
                propertyOptions={propertyOptions}
              />
            )
            : <div className="card muted">Избери услуга отляво.</div>}
        </div>
      </div>
    </div>
  );
}

/** Огледало на server/src/services/serviceConsumables.js:parseCopies — за преизчисление докато операторът пише. */
function parseCopies(text) {
  if (!text) return null;
  const digits = String(text).replace(/\D/g, '');
  return digits ? Number(digits) : null;
}
function roundUpTo10(grams) { return Math.ceil(grams / 10) * 10; }
function liveSuggestGrams(copies, isColor) {
  if (!copies) return null;
  return roundUpTo10((copies / 1000) * (isColor ? 40 : 50));
}

export function ServiceReview({ serviceId, onSaved, propertyOptions }) {
  const [detail, setDetail] = useState(null);
  const [toners, setToners] = useState([]);
  const [chips, setChips] = useState([]);
  const [printType, setPrintType] = useState('');
  const [color, setColor] = useState('');
  const [capacityText, setCapacityText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/api/service-consumables/${serviceId}`).then(applyDetail);
  }, [serviceId]);

  function applyDetail(d) {
    setDetail(d);
    setPrintType(d.printType ?? '');
    setColor(d.color ?? '');
    setCapacityText(d.capacityText ?? '');
    const savedToners = d.linked.filter((l) => l.kind === 'toner')
      .map((l) => ({ productId: l.productId, name: l.name, grams: l.grams }));
    setToners(savedToners.length ? savedToners
      : []);
    const savedChips = d.linked.filter((l) => l.kind === 'chip')
      .map((l) => ({ productId: l.productId, name: l.name }));
    setChips(savedChips);
  }

  if (!detail) return <div className="card muted">Зареждане…</div>;

  // Преизчислено на лету от полето, докато операторът пише — не чака запис.
  const liveCopies = parseCopies(capacityText);
  const isColor = printType === 'Цветен';
  const suggestedGrams = liveSuggestGrams(liveCopies, isColor);
  const tonerSuggestions = detail.suggestions.filter((s) => !s.looksLikeChip);
  const chipSuggestions = detail.suggestions.filter((s) => s.looksLikeChip);

  function addToner(productId, name) {
    if (toners.some((t) => t.productId === productId)) return;
    if (chips.some((c) => c.productId === productId)) {
      setError('Вече е закачена като чип — не може да е и двете.');
      return;
    }
    setToners([...toners, { productId, name, grams: suggestedGrams }]);
  }
  function removeToner(productId) {
    setToners(toners.filter((t) => t.productId !== productId));
  }
  function setGrams(productId, grams) {
    setToners(toners.map((t) => (t.productId === productId ? { ...t, grams } : t)));
  }
  function addChip(productId, name) {
    if (chips.some((c) => c.productId === productId)) return;
    if (toners.some((t) => t.productId === productId)) {
      setError('Вече е закачена като тонер — не може да е и двете.');
      return;
    }
    setChips([...chips, { productId, name }]);
  }
  function removeChip(productId) {
    setChips(chips.filter((c) => c.productId !== productId));
  }

  async function save() {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const fresh = await api.put(`/api/service-consumables/${serviceId}`, {
        toners: toners.map((t) => ({ productId: t.productId, grams: t.grams ?? null })),
        chips: chips.map((c) => ({ productId: c.productId })),
        capacityText,
        printType,
        color,
      });
      applyDetail(fresh);
      setSaved(true);
      await onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{detail.name}</h3>

      <div className="field-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <label style={{ whiteSpace: 'nowrap' }}>Капацитет</label>
        <input
          value={capacityText}
          placeholder="напр. 1000 копия"
          autoComplete="off"
          style={{ width: 160 }}
          onChange={(e) => { setCapacityText(e.target.value); setSaved(false); }}
        />
        {!liveCopies && <span className="muted">без число тук няма как да смятам грамове</span>}
      </div>
      {!detail.capacityCopies && !liveCopies && (
        <div className="note" style={{ marginTop: 8 }}>
          Няма зададена стойност за капацитет (брой копия) — без нея
          изписването на тонера няма да бъде вярно. Впиши го отгоре и
          запиши — попада направо в картона на услугата.
        </div>
      )}

      <div className="field-row" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Тип печат
          <select
            value={printType}
            onChange={(e) => { setPrintType(e.target.value); setSaved(false); }}
            style={{ width: 130 }}
          >
            <option value="">—</option>
            {(propertyOptions.printType ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Цвят
          <select
            value={color}
            onChange={(e) => { setColor(e.target.value); setSaved(false); }}
            style={{ width: 130 }}
          >
            <option value="">—</option>
            {(propertyOptions.color ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </label>
        {liveCopies
          ? <span className="muted">предложение: {suggestedGrams} гр. ({isColor ? '40гр' : '50гр'}/1000)</span>
          : <span className="muted">без капацитет не смятам грамове, впиши ги ръчно</span>}
      </div>
      {!printType && (
        <div className="muted" style={{ marginTop: 4 }}>
          Няма зададен „Тип печат" — предложенията по-долу не се
          филтрират по цвят, докато не го впишеш.
        </div>
      )}

      <h4>Тонер</h4>
      {toners.length === 0 && <div className="muted">Няма закачен тонер.</div>}
      {toners.map((t) => (
        <div key={t.productId} className="field-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{t.name}</span>
          <input
            type="number"
            value={t.grams ?? ''}
            onChange={(e) => setGrams(t.productId, e.target.value ? Number(e.target.value) : null)}
            style={{ width: 70 }}
          />
          <span className="muted">гр.</span>
          <button className="link" onClick={() => removeToner(t.productId)}>махни</button>
        </div>
      ))}

      {tonerSuggestions.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="muted">от историята:</div>
          {tonerSuggestions.map((s) => (
            <button
              key={s.productId}
              className="chip"
              disabled={toners.some((t) => t.productId === s.productId)
                || chips.some((c) => c.productId === s.productId)}
              onClick={() => addToner(s.productId, s.name)}
              title={`${s.times}× на същия документ${s.color ? ` — ${s.color}` : ''}`}
            >
              + {s.name} ({s.times}×{s.color ? `, ${s.color}` : ''})
            </button>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <ProductLookup
          placeholder="добави друг тонер ръчно"
          onPick={(id, name) => id && addToner(id, name)}
        />
      </div>

      <h4>Чип / зануляване</h4>
      {chips.length === 0 && <div className="muted">Няма закачен чип.</div>}
      {chips.map((c) => (
        <div key={c.productId} className="field-row" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{c.name}</span>
          <button className="link" onClick={() => removeChip(c.productId)}>махни</button>
        </div>
      ))}

      {chipSuggestions.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div className="muted">от историята:</div>
          {chipSuggestions.map((s) => (
            <button
              key={s.productId}
              className="chip"
              disabled={chips.some((c) => c.productId === s.productId)
                || toners.some((t) => t.productId === s.productId)}
              onClick={() => addChip(s.productId, s.name)}
              title={`${s.times}× на същия документ${s.color ? ` — ${s.color}` : ''}`}
            >
              + {s.name} ({s.times}×{s.color ? `, ${s.color}` : ''})
            </button>
          ))}
        </div>
      )}

      <div style={{ marginTop: 8 }}>
        <ProductLookup
          placeholder="добави чип ръчно"
          onPick={(id, name) => id && addChip(id, name)}
        />
      </div>

      {error && <div className="error">{error}</div>}

      <div style={{ marginTop: 16, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? 'Записвам…' : 'Запиши'}
        </button>
        {saved && <span className="badge ok">Записано</span>}
      </div>
    </div>
  );
}
